import { observer } from "mobx-react";
import { TMessage } from "@/i18n/tt";
import { IconName } from "./iconMapping";
import { FgColor } from "./colors";
import { useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import styles from "./MenuBar.module.css";
import { Box, VBox } from "./Box";
import { Icon, IconProps } from "./Icon";
import { renderIcon } from "./iconHelpers";
import { Tag, TagColor } from "./Tag";
import { BetaLabel } from "./BetaLabel";
import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";

export type MenuLinkProps = {
  url?: string;
  text: TMessage;
  icon?: IconName;
  iconRotate?: number;
  hoverIcon?: IconName | IconProps;
  badge?: TMessage;
  badgeColor?: FgColor;
  isActive?: boolean;
  tag?: TMessage;
  tagColor?: TagColor;
  onActivate?: () => void;
  disabled?: boolean;
};

export const MenuItem = observer(function MenuLink({
  url,
  text,
  icon,
  badge,
  badgeColor,
  isActive,
  tag,
  tagColor = "error",
  onActivate,
  iconRotate,
  hoverIcon,
  disabled,
}: MenuLinkProps) {
  const { t } = useTranslation();
  const className = classNames(
    "group",
    "p-2",
    "rounded-lg",
    "no-underline",
    "color-default",
    "active:color-default",
    "visited:color-default",
    !disabled && ["hover:color-default", "hover:bg-alt1"],
    isActive && "font-bold bg-alt2-dimmed",
  );
  const content = (
    <Box
      alignItems={"center"}
      gap={"sm"}
      whitespace={"nowrap"}
      className={disabled ? "subtle" : undefined}
    >
      {icon && (
        <Icon
          className={classNames(
            styles.linkIcon,
            hoverIcon && "group-hover:hidden group-focus-visible:hidden",
          )}
          name={icon}
          color={disabled ? "subtle" : isActive ? "info" : "default"}
          rotate={iconRotate}
        />
      )}
      {hoverIcon && (
        <span className={"hidden group-hover:flex group-focus-visible:flex"}>
          {renderIcon(hoverIcon)}
        </span>
      )}
      <span className={styles.linkText}>{t(text)}</span>
      {tag && (
        <Box marginLeft={"auto"}>
          <Tag
            compact
            label={t(tag)}
            color={tagColor}
            className={isActive ? "font-normal" : undefined}
          />
        </Box>
      )}
      {badge && <BetaLabel label={badge} color={badgeColor} />}
    </Box>
  );

  if (url) {
    return (
      <a href={url} className={className} data-collapsed-tooltip={t(text)}>
        {content}
      </a>
    );
  }
  return (
    <button
      className={className}
      data-collapsed-tooltip={t(text)}
      onClick={onActivate}
      disabled={disabled}
    >
      {content}
    </button>
  );
});

export const MenuGroup = observer(function MenuGroup({
  text,
  icon,
  iconRotate,
  items,
  isOpen: openGroup,
}: {
  text: TMessage;
  icon?: IconName;
  iconRotate?: number;
  items: MenuLinkProps[];
  isOpen?: boolean;
}) {
  const [openState, setOpen] = useState(true);
  const isOpen = openGroup ?? openState;
  const caret = { name: "caret-down" as const, rotate: isOpen ? 0 : -90 };
  return (
    <VBox>
      <MenuItem
        icon={icon ?? caret.name}
        iconRotate={icon ? iconRotate : caret.rotate}
        hoverIcon={icon && caret}
        text={text}
        onActivate={() => {
          if (!isOpen) items[0]?.onActivate?.();
          setOpen(!isOpen);
        }}
      />
      {isOpen && (
        <VBox marginLeft={"lg"}>
          {items.map((item, index) => (
            <MenuItem key={index} {...item} />
          ))}
        </VBox>
      )}
    </VBox>
  );
});

function updateMenuSize(container: HTMLDivElement) {
  const items = Array.from(container.querySelectorAll<HTMLElement>("a, button"));

  if (container.scrollWidth > container.offsetWidth) {
    for (const link of items.toReversed()) {
      link.classList.add(styles.collapsed);
      if (container.scrollWidth <= container.offsetWidth) {
        break;
      }
    }
  } else {
    for (const item of items) {
      item.classList.remove(styles.collapsed);
      if (container.scrollWidth > container.offsetWidth) {
        item.classList.add(styles.collapsed);
        break;
      }
    }
  }

  for (const item of items) {
    if (item.classList.contains(styles.collapsed)) {
      item.dataset.tooltip = item.dataset.collapsedTooltip;
    } else {
      delete item.dataset.tooltip;
    }
  }
}

export type MenuBarProps = {
  items: MenuLinkProps[];
  containerRef: HTMLElement | null;
};

export const MenuBar = observer(({ items, containerRef }: MenuBarProps) => {
  const { t } = useTranslation();
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef;
    const itemsContainer = itemsContainerRef.current;
    if (!container || !itemsContainer) {
      return;
    }
    const o = new ResizeObserver(debounce(() => updateMenuSize(itemsContainer), 100));
    o.observe(container);
    return () => {
      o.disconnect();
    };
  }, [containerRef]);

  return (
    <Box
      overflow={"hidden"}
      alignItems={"center"}
      ref={itemsContainerRef}
      paddingX={"sm"}
      className={"grow-1000 shrink"}
    >
      {items.map((item) => (
        <MenuItem key={t(item.text)} {...item} />
      ))}
    </Box>
  );
});
